import { type CSSProperties, type ImgHTMLAttributes, useState } from 'react'

import { cn } from '@/lib/utils'
import ImageLightboxGallery, {
  type LightboxItem,
  type LightboxTriggerMode,
  type LightboxTriggerOptions,
  type LightboxTriggerProps,
} from './ImageLightboxGallery'

/** A preview view, including historical snapshots whose metadata has not been validated. */
export interface LightboxPreviewMedia {
  url?: unknown
  id?: unknown
  width?: unknown
  height?: unknown
}

interface MeasuredDimensions {
  width: number
  height: number
}

interface ImageLightboxPreviewTriggerProps {
  media: LightboxPreviewMedia | null | undefined
  alt: string
  caption?: string
  /** A link also opens the photo without JavaScript; a button only opens the lightbox. */
  triggerMode?: LightboxTriggerMode
  /** Attributes for the trigger this preview's own lightbox opens from. */
  elementProps?: Omit<LightboxTriggerOptions, 'mode'> | null
  /** A trigger from a gallery around this preview; the preview then opens that gallery. */
  triggerProps?: LightboxTriggerProps | null
  frameClassName?: string
  frameStyle?: CSSProperties
  imageClassName?: string
  imageStyle?: CSSProperties
  imageLoading?: ImgHTMLAttributes<HTMLImageElement>['loading']
  triggerClassName?: string
}

function getPositiveDimension(value: unknown) {
  try {
    const number = Number(value)

    return Number.isFinite(number) && number > 0 ? number : null
  } catch {
    // Historical snapshot metadata may not support numeric coercion.
    return null
  }
}

function getNaturalImageDimensions(image: HTMLImageElement | null | undefined) {
  const width = getPositiveDimension(image?.naturalWidth)
  const height = getPositiveDimension(image?.naturalHeight)

  if (!width || !height) {
    return null
  }

  return { width, height }
}

function hasStoredDimensions(media: LightboxPreviewMedia | null | undefined) {
  return Boolean(getPositiveDimension(media?.width) && getPositiveDimension(media?.height))
}

function mergeMeasuredDimensions(
  media: LightboxPreviewMedia | null | undefined,
  measuredDimensions: MeasuredDimensions | null
) {
  if (!media?.url || !measuredDimensions || hasStoredDimensions(media)) {
    return media
  }

  return {
    ...media,
    width: getPositiveDimension(media.width) ?? measuredDimensions.width,
    height: getPositiveDimension(media.height) ?? measuredDimensions.height,
  }
}

export function createLightboxPreviewItem({
  media,
  caption,
  id,
  alt,
}: {
  media: LightboxPreviewMedia | null | undefined
  caption?: string
  id?: number | string
  alt?: string
}): LightboxItem | null {
  if (typeof media?.url !== 'string' || !media.url) {
    return null
  }

  const url = media.url
  const width = media.width
  const height = media.height

  return {
    id: id ?? media.id ?? caption ?? alt ?? media.url,
    src: url,
    url,
    width,
    height,
    w: width,
    h: height,
    caption,
  }
}

const ImageLightboxPreviewTrigger = ({
  media,
  alt,
  caption,
  triggerMode = 'anchor',
  elementProps = null,
  triggerProps = null,
  frameClassName = '',
  frameStyle = undefined,
  imageClassName = '',
  imageStyle = undefined,
  imageLoading = 'lazy',
  triggerClassName = '',
}: ImageLightboxPreviewTriggerProps) => {
  const [measuredImage, setMeasuredImage] = useState<(MeasuredDimensions & { url: string }) | null>(
    null
  )
  const frameClassNames = cn(
    'relative inline-block shrink-0 overflow-hidden rounded-lg',
    frameClassName
  )
  const measuredDimensions = measuredImage?.url === media?.url ? measuredImage : null
  const mediaWithDimensions = mergeMeasuredDimensions(media, measuredDimensions)

  if (typeof media?.url !== 'string' || !media.url) {
    return (
      <div className={frameClassNames} style={frameStyle}>
        <div aria-hidden="true" className="h-full w-full bg-muted/60" />
      </div>
    )
  }

  const mediaUrl = media.url
  const TriggerTag = triggerMode === 'button' ? 'button' : 'a'

  const recordImageDimensions = (image: HTMLImageElement | null | undefined) => {
    const dimensions = getNaturalImageDimensions(image)

    if (!dimensions) {
      return null
    }

    setMeasuredImage((current) => {
      if (
        current?.url === mediaUrl &&
        current.width === dimensions.width &&
        current.height === dimensions.height
      ) {
        return current
      }

      return {
        url: mediaUrl,
        ...dimensions,
      }
    })

    return dimensions
  }

  const applyTriggerDimensions = (trigger: HTMLElement) => {
    if (triggerMode !== 'anchor' || hasStoredDimensions(media)) {
      return
    }

    const dimensions = recordImageDimensions(trigger?.querySelector?.('img'))

    if (!dimensions) {
      return
    }

    trigger.setAttribute('data-pswp-width', String(dimensions.width))
    trigger.setAttribute('data-pswp-height', String(dimensions.height))
  }

  const renderTrigger = (props: LightboxTriggerProps) => {
    const { onClickCapture, ...restProps } = props

    return (
      <TriggerTag
        {...restProps}
        aria-label={caption ? `Preview ${caption}` : `Preview ${alt}`}
        className={cn(
          frameClassNames,
          'z-0 cursor-pointer focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden',
          triggerClassName
        )}
        style={frameStyle}
        onClickCapture={(event) => {
          onClickCapture?.(event)

          if (!event.defaultPrevented) {
            applyTriggerDimensions(event.currentTarget)
          }
        }}
      >
        <img
          src={mediaUrl}
          alt={alt}
          loading={imageLoading}
          decoding="async"
          className={cn('h-full w-full', imageClassName)}
          style={imageStyle}
          onLoad={(event) => {
            recordImageDimensions(event.currentTarget)
          }}
        />
        {caption && triggerMode === 'anchor' ? (
          <span className="pswp-caption-content hidden">{caption}</span>
        ) : null}
      </TriggerTag>
    )
  }

  if (triggerProps) {
    return renderTrigger(triggerProps)
  }

  const previewItem = createLightboxPreviewItem({
    media: mediaWithDimensions,
    caption,
    alt,
  })

  return (
    <ImageLightboxGallery items={previewItem ? [previewItem] : []} className="contents">
      {({ getTriggerProps }) =>
        renderTrigger(
          getTriggerProps(0, {
            ...elementProps,
            mode: triggerMode,
          })
        )
      }
    </ImageLightboxGallery>
  )
}

export default ImageLightboxPreviewTrigger
